import { AnchorButton, Button, Spinner, SpinnerSize } from "@blueprintjs/core";
import { Popover2 } from "@blueprintjs/popover2";
import { observer } from "mobx-react";
import React, { useContext, useEffect, useState } from "react";
import type { Positions } from ".";
import { StoresContext } from "../../../../pages/_app";
import { TreeNode, NODE_NAME, NODE_CATALOG_NUMBER } from "../../../../stores/catalog-tree-store";
import { catalogItemDisplayName, genomeToDownloadInfos } from "../../../../utils/genomes";
import {
  DownloadInfo,
  DownloadSelectedPopoverContent,
  DownloadType,
  showSuccessNotification,
} from "../download-selected-button";
import { DATA_BUTTONS_GAP, EXTERNAL_LINK_GAP, NAME_GAP, NAME_LINE_PADDING } from "./constants";

export const GenomesTreeDataButtons = ({
  indexes,
  nodes,
  positions,
  maxTextLength,
  getTextLength,
  getUuid,
  isDerivative,
}: {
  indexes: number[];
  maxTextLength: number;
  nodes: TreeNode[];
  positions: Positions;
  getUuid: (idx: number) => string | null;
  getTextLength: (idx: number) => number;
  isDerivative: (idx: number) => boolean;
}) => {
  return (
    <g className="genomes-tree__nodes__buttons">
      {indexes.map((idx) => {
        const p = positions[idx];
        const len = getTextLength(idx);
        const uuid = getUuid(idx);
        if (isDerivative(idx)) {
          return null;
        }
        return (
          <React.Fragment key={`${idx}-${maxTextLength.toFixed(4)}`}>
            <text dominantBaseline="middle" x={p[0] + NAME_GAP} y={p[1]}>
              {catalogItemDisplayName(nodes[idx][NODE_NAME], nodes[idx][NODE_CATALOG_NUMBER])}
            </text>
            <line
              x1={p[0] + NAME_GAP + NAME_LINE_PADDING + EXTERNAL_LINK_GAP + len}
              y1={p[1]}
              x2={p[0] + NAME_GAP + maxTextLength + DATA_BUTTONS_GAP - NAME_LINE_PADDING}
              y2={p[1]}
            />
            <foreignObject
              style={{
                transform: `translate(${p[0] + NAME_GAP + maxTextLength + DATA_BUTTONS_GAP}px, ${
                  p[1] - 13
                }px)`,
              }}
              width={128 + (uuid ? 128 + 10 : 0)}
              height={24}
            >
              <div>
                {uuid ? (
                  <>
                    <DownloadButton uuid={uuid} />
                    <AnchorButton
                      small
                      target="_blank"
                      rightIcon="chevron-right"
                      href={`/genomes/${uuid}`}
                    >
                      View Genome
                    </AnchorButton>
                  </>
                ) : (
                  <AnchorButton
                    small
                    icon="document-open"
                    href={
                      "https://www.atcc.org/applications/reference-quality-data/discover-the-atcc-genome-portal#gen456"
                    }
                    target="_blank"
                  >
                    Request NGS
                  </AnchorButton>
                )}
              </div>
            </foreignObject>
          </React.Fragment>
        );
      })}
    </g>
  );
};

const DownloadButton = observer(({ uuid }: { uuid: string }) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [downloadInfos, setDownloadInfos] = useState<DownloadInfo[] | null>(null);
  const [downloadTypes, setDownloadTypes] = useState<DownloadType[]>([]);
  const rootStore = useContext(StoresContext);

  useEffect(() => {
    if (isOpen && downloadInfos === null) {
      setIsLoading(true);
      rootStore.annotationStore.fetchGenomeDetails(uuid).then(() => {
        if (rootStore.annotationStore.genome) {
          const infos = genomeToDownloadInfos(rootStore.annotationStore.genome);
          setDownloadInfos(infos);
          setDownloadTypes(infos.map((info) => info.type));
        }
        setIsLoading(false);
      });
    }
  }, [isOpen]);

  async function callInitBulkDownload() {
    setIsDownloading(true);
    // Let's make a local copy of downloadTypes.
    return rootStore.genomeStore
      .initSingleGenomeDownload(uuid, [...downloadTypes])
      .then(({ error }) => {
        if (error === undefined) {
          showSuccessNotification(rootStore);
          return null;
        } else {
          return error;
        }
      })
      .finally(() => {
        setIsDownloading(false);
      });
  }

  const handleDownload = () => {
    setIsOpen(false);
    rootStore.userStore.tryToDoGuardedAction(callInitBulkDownload, true);
  };

  return (
    <Popover2
      placement={"bottom-end"}
      isOpen={isOpen}
      lazy={true}
      onClose={() => setIsOpen(false)}
      content={
        <div className="genomes-tree__nodes__download-tooltip">
          {isLoading || downloadInfos === null ? (
            <Spinner size={SpinnerSize.SMALL} />
          ) : (
            <DownloadSelectedPopoverContent
              downloadTypes={downloadTypes}
              downloadInfos={downloadInfos}
              initSingleGenomeSearch={rootStore.genomeStore.initSingleGenomeSearch}
              uuid={uuid}
              isLoading={isDownloading}
              onDownload={handleDownload}
              onChange={setDownloadTypes}
            />
          )}
        </div>
      }
    >
      <Button small icon="download" onClick={() => setIsOpen((prevVal) => !prevVal)}>
        Download
      </Button>
    </Popover2>
  );
});
